"use client";

import { useState } from "react";
import Link from "next/link";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getBlockedState, getBlockedZip } from "@/config/shippingRestrictions";

interface FormData {
  // Shipping
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  apartment: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  // Billing
  billingSameAsShipping: boolean;
  billingFirstName: string;
  billingLastName: string;
  billingAddress: string;
  billingApartment: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  billingCountry: string;
  // Payment
  cardName: string;
  cardNumber: string;
  cardExpiry: string;
  cardCvc: string;
}

const initialForm: FormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  apartment: "",
  city: "",
  state: "",
  zip: "",
  country: "US",
  billingSameAsShipping: true,
  billingFirstName: "",
  billingLastName: "",
  billingAddress: "",
  billingApartment: "",
  billingCity: "",
  billingState: "",
  billingZip: "",
  billingCountry: "US",
  cardName: "",
  cardNumber: "",
  cardExpiry: "",
  cardCvc: "",
};

const ORDER_SUMMARY = {
  items: [{ name: "Kratapillar Premium Kratom Powder", variant: "Green Line", qty: 1, price: 89.99 }],
  subtotal: 89.99,
  shipping: 0,
  total: 89.99,
};

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY",
];

const inputClass =
  "w-full px-4 py-3 text-sm outline-none transition-all duration-200 bg-white";
const inputStyle = {
  border: "1px solid #E0DFDB",
  fontFamily: "Inter, system-ui, sans-serif",
  color: "#1F1F1F",
  borderRadius: "2px",
};
const inputFocusStyle = {
  border: "1px solid #3F5A45",
};

function FormField({
  label,
  id,
  type = "text",
  value,
  onChange,
  placeholder,
  required,
  autoComplete,
}: {
  label: string;
  id: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  autoComplete?: string;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
        style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif", letterSpacing: "0.06em" }}
      >
        {label}
        {required && <span style={{ color: "#3F5A45" }}> *</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoComplete={autoComplete}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={inputClass}
        style={{ ...inputStyle, ...(focused ? inputFocusStyle : {}) }}
      />
    </div>
  );
}

function SelectField({
  label,
  id,
  value,
  onChange,
  options,
  required,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
        style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif", letterSpacing: "0.06em" }}
      >
        {label}
        {required && <span style={{ color: "#3F5A45" }}> *</span>}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={inputClass}
        style={{ ...inputStyle, ...(focused ? inputFocusStyle : {}), appearance: "none", cursor: "pointer" }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function CheckoutPage() {
  const [form, setForm] = useState<FormData>(initialForm);
  const [submitted, setSubmitted] = useState(false);

  const setField = (key: keyof FormData) => (value: string | boolean) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Real-time shipping restriction check
  const blockedState = getBlockedState(form.state);
  const blockedZip = form.zip.length >= 3 ? getBlockedZip(form.zip) : null;
  const shippingBlocked = !!(blockedState || blockedZip);
  const blockedLocation = blockedState || blockedZip || "";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (shippingBlocked) return;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div style={{ backgroundColor: "#F6F5F2" }}>
        <Header />
        <main className="min-h-screen flex items-center justify-center px-6 py-24">
          <div className="text-center max-w-md">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: "rgba(63,90,69,0.1)" }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="#3F5A45"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h1
              className="font-serif font-bold mb-3"
              style={{
                fontFamily: "Fraunces, Georgia, serif",
                fontSize: "2rem",
                letterSpacing: "-0.03em",
                color: "#1F1F1F",
              }}
            >
              Order Placed!
            </h1>
            <p
              className="text-sm mb-8 leading-relaxed"
              style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
            >
              Thank you, {form.firstName}. Your order has been received and will ship within 24 hours. A confirmation will be sent to {form.email}.
            </p>
            <Link href="/homepage" className="btn-primary">
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "#F6F5F2" }}>
      <Header />
      <main className="min-h-screen">
        {/* Page Header */}
        <div
          className="py-12 border-b"
          style={{ borderColor: "#E0DFDB", backgroundColor: "#F6F5F2" }}
        >
          <div className="max-w-container mx-auto px-6 lg:px-8">
            <span className="section-label mb-3 block">Almost There</span>
            <h1
              className="font-serif font-bold"
              style={{
                fontFamily: "Fraunces, Georgia, serif",
                fontSize: "clamp(2rem, 4vw, 3rem)",
                letterSpacing: "-0.03em",
                lineHeight: "1.0",
                color: "#1F1F1F",
              }}
            >
              Checkout
            </h1>
          </div>
        </div>

        <div className="max-w-container mx-auto px-6 lg:px-8 py-12">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
              {/* Left: Forms */}
              <div className="lg:col-span-2 space-y-10">
                {/* Shipping Information */}
                <section>
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: "#3F5A45", fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      1
                    </div>
                    <h2
                      className="font-serif font-semibold"
                      style={{
                        fontFamily: "Fraunces, Georgia, serif",
                        fontSize: "1.25rem",
                        color: "#1F1F1F",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      Shipping Information
                    </h2>
                  </div>

                  <div
                    className="p-6 space-y-5"
                    style={{ backgroundColor: "white", border: "1px solid #E0DFDB" }}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <FormField
                        label="First Name"
                        id="firstName"
                        value={form.firstName}
                        onChange={setField("firstName") as (v: string) => void}
                        placeholder="Jane"
                        required
                        autoComplete="given-name"
                      />
                      <FormField
                        label="Last Name"
                        id="lastName"
                        value={form.lastName}
                        onChange={setField("lastName") as (v: string) => void}
                        placeholder="Smith"
                        required
                        autoComplete="family-name"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <FormField
                        label="Email Address"
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={setField("email") as (v: string) => void}
                        placeholder="jane@example.com"
                        required
                        autoComplete="email"
                      />
                      <FormField
                        label="Phone Number"
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={setField("phone") as (v: string) => void}
                        placeholder="+1 (555) 000-0000"
                        autoComplete="tel"
                      />
                    </div>
                    <FormField
                      label="Street Address"
                      id="address"
                      value={form.address}
                      onChange={setField("address") as (v: string) => void}
                      placeholder="123 Main Street"
                      required
                      autoComplete="street-address"
                    />
                    <FormField
                      label="Apartment, Suite, etc. (optional)"
                      id="apartment"
                      value={form.apartment}
                      onChange={setField("apartment") as (v: string) => void}
                      placeholder="Apt 4B"
                      autoComplete="address-line2"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                      <FormField
                        label="City"
                        id="city"
                        value={form.city}
                        onChange={setField("city") as (v: string) => void}
                        placeholder="New York"
                        required
                        autoComplete="address-level2"
                      />
                      <SelectField
                        label="State"
                        id="state"
                        value={form.state}
                        onChange={setField("state") as (v: string) => void}
                        required
                        options={[
                          { value: "", label: "Select state" },
                          ...US_STATES.map((s) => ({ value: s, label: s })),
                        ]}
                      />
                      <FormField
                        label="ZIP Code"
                        id="zip"
                        value={form.zip}
                        onChange={setField("zip") as (v: string) => void}
                        placeholder="10001"
                        required
                        autoComplete="postal-code"
                      />
                    </div>

                    {/* Shipping restriction inline error */}
                    {shippingBlocked && (
                      <div
                        className="flex items-start gap-2 p-3 text-sm"
                        style={{
                          backgroundColor: "#FFF2F2",
                          border: "1px solid #FFCDD2",
                          borderRadius: "2px",
                          color: "#C62828",
                          fontFamily: "Inter, system-ui, sans-serif",
                          lineHeight: "1.5",
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: "1px" }}>
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                          <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                        <span>
                          Sorry, we cannot ship to your location. Kratom sales are restricted in {blockedLocation}.{" "}
                          <Link
                            href="/shipping-returns"
                            className="underline font-semibold"
                            style={{ color: "#C62828" }}
                          >
                            See our Shipping Policy for details.
                          </Link>
                        </span>
                      </div>
                    )}

                    <SelectField
                      label="Country"
                      id="country"
                      value={form.country}
                      onChange={setField("country") as (v: string) => void}
                      required
                      options={[
                        { value: "US", label: "United States" },
                        { value: "CA", label: "Canada" },
                      ]}
                    />
                  </div>
                </section>

                {/* Billing Information */}
                <section>
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: "#3F5A45", fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      2
                    </div>
                    <h2
                      className="font-serif font-semibold"
                      style={{
                        fontFamily: "Fraunces, Georgia, serif",
                        fontSize: "1.25rem",
                        color: "#1F1F1F",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      Billing Information
                    </h2>
                  </div>

                  <div
                    className="p-6"
                    style={{ backgroundColor: "white", border: "1px solid #E0DFDB" }}
                  >
                    {/* Same as shipping toggle */}
                    <label className="flex items-center gap-3 cursor-pointer mb-6">
                      <div
                        className="relative w-5 h-5 flex-shrink-0"
                        style={{ border: "1.5px solid #E0DFDB", borderRadius: "2px", backgroundColor: form.billingSameAsShipping ? "#3F5A45" : "white", borderColor: form.billingSameAsShipping ? "#3F5A45" : "#E0DFDB", transition: "all 0.2s" }}
                        onClick={() => setField("billingSameAsShipping")(!form.billingSameAsShipping)}
                      >
                        {form.billingSameAsShipping && (
                          <svg className="absolute inset-0 m-auto" width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={form.billingSameAsShipping}
                        onChange={(e) => setField("billingSameAsShipping")(e.target.checked)}
                      />
                      <span
                        className="text-sm"
                        style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}
                      >
                        Same as shipping address
                      </span>
                    </label>

                    {!form.billingSameAsShipping && (
                      <div className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <FormField
                            label="First Name"
                            id="billingFirstName"
                            value={form.billingFirstName}
                            onChange={setField("billingFirstName") as (v: string) => void}
                            placeholder="Jane"
                            required
                          />
                          <FormField
                            label="Last Name"
                            id="billingLastName"
                            value={form.billingLastName}
                            onChange={setField("billingLastName") as (v: string) => void}
                            placeholder="Smith"
                            required
                          />
                        </div>
                        <FormField
                          label="Street Address"
                          id="billingAddress"
                          value={form.billingAddress}
                          onChange={setField("billingAddress") as (v: string) => void}
                          placeholder="123 Main Street"
                          required
                        />
                        <FormField
                          label="Apartment, Suite, etc. (optional)"
                          id="billingApartment"
                          value={form.billingApartment}
                          onChange={setField("billingApartment") as (v: string) => void}
                          placeholder="Apt 4B"
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                          <FormField
                            label="City"
                            id="billingCity"
                            value={form.billingCity}
                            onChange={setField("billingCity") as (v: string) => void}
                            placeholder="New York"
                            required
                          />
                          <SelectField
                            label="State"
                            id="billingState"
                            value={form.billingState}
                            onChange={setField("billingState") as (v: string) => void}
                            required
                            options={[
                              { value: "", label: "Select state" },
                              ...US_STATES.map((s) => ({ value: s, label: s })),
                            ]}
                          />
                          <FormField
                            label="ZIP Code"
                            id="billingZip"
                            value={form.billingZip}
                            onChange={setField("billingZip") as (v: string) => void}
                            placeholder="10001"
                            required
                          />
                        </div>
                        <SelectField
                          label="Country"
                          id="billingCountry"
                          value={form.billingCountry}
                          onChange={setField("billingCountry") as (v: string) => void}
                          required
                          options={[
                            { value: "US", label: "United States" },
                            { value: "CA", label: "Canada" },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                </section>

                {/* Payment */}
                <section>
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: "#3F5A45", fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      3
                    </div>
                    <h2
                      className="font-serif font-semibold"
                      style={{
                        fontFamily: "Fraunces, Georgia, serif",
                        fontSize: "1.25rem",
                        color: "#1F1F1F",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      Payment Details
                    </h2>
                  </div>

                  <div
                    className="p-6 space-y-5"
                    style={{ backgroundColor: "white", border: "1px solid #E0DFDB" }}
                  >
                    <div
                      className="flex items-center gap-2 p-3 text-xs"
                      style={{
                        backgroundColor: "rgba(63,90,69,0.06)",
                        border: "1px solid rgba(63,90,69,0.2)",
                        borderRadius: "2px",
                        color: "#3F5A45",
                        fontFamily: "Inter, system-ui, sans-serif",
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                      Your payment information is encrypted and secure.
                    </div>

                    <FormField
                      label="Name on Card"
                      id="cardName"
                      value={form.cardName}
                      onChange={setField("cardName") as (v: string) => void}
                      placeholder="Jane Smith"
                      required
                      autoComplete="cc-name"
                    />
                    <FormField
                      label="Card Number"
                      id="cardNumber"
                      value={form.cardNumber}
                      onChange={setField("cardNumber") as (v: string) => void}
                      placeholder="1234 5678 9012 3456"
                      required
                      autoComplete="cc-number"
                    />
                    <div className="grid grid-cols-2 gap-5">
                      <FormField
                        label="Expiry Date"
                        id="cardExpiry"
                        value={form.cardExpiry}
                        onChange={setField("cardExpiry") as (v: string) => void}
                        placeholder="MM / YY"
                        required
                        autoComplete="cc-exp"
                      />
                      <FormField
                        label="CVC"
                        id="cardCvc"
                        value={form.cardCvc}
                        onChange={setField("cardCvc") as (v: string) => void}
                        placeholder="123"
                        required
                        autoComplete="cc-csc"
                      />
                    </div>
                  </div>
                </section>

                {/* Submit */}
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <Link
                    href="/cart"
                    className="inline-flex items-center gap-2 text-sm transition-colors"
                    style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M19 12H5M12 5l-7 7 7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Back to Cart
                  </Link>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={shippingBlocked}
                    style={shippingBlocked ? { opacity: 0.45, cursor: "not-allowed" } : {}}
                  >
                    Place Order — ${ORDER_SUMMARY.total.toFixed(2)}
                  </button>
                </div>
              </div>

              {/* Right: Order Summary */}
              <div className="lg:col-span-1">
                <div
                  className="p-6 sticky top-24"
                  style={{ backgroundColor: "white", border: "1px solid #E0DFDB" }}
                >
                  <h2
                    className="font-serif font-semibold mb-6"
                    style={{
                      fontFamily: "Fraunces, Georgia, serif",
                      fontSize: "1.25rem",
                      color: "#1F1F1F",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Order Summary
                  </h2>

                  {/* Items */}
                  <div className="space-y-4 mb-6">
                    {ORDER_SUMMARY.items.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div
                          className="w-12 h-12 flex-shrink-0 flex items-center justify-center"
                          style={{ backgroundColor: "rgba(63,90,69,0.08)", border: "1px solid rgba(63,90,69,0.2)" }}
                        >
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#3F5A45" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-xs font-semibold leading-tight"
                            style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}
                          >
                            {item.name}
                          </p>
                          <p
                            className="text-xs mt-0.5"
                            style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                          >
                            {item.variant} · Qty {item.qty}
                          </p>
                        </div>
                        <span
                          className="text-sm font-semibold flex-shrink-0"
                          style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}
                        >
                          ${(item.price * item.qty).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div
                    className="border-t pt-4 space-y-3"
                    style={{ borderColor: "#E0DFDB" }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm" style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}>
                        Subtotal
                      </span>
                      <span className="text-sm font-semibold" style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}>
                        ${ORDER_SUMMARY.subtotal.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm" style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}>
                        Shipping
                      </span>
                      <span className="text-sm font-semibold" style={{ color: "#3F5A45", fontFamily: "Inter, system-ui, sans-serif" }}>
                        Free
                      </span>
                    </div>
                    <div
                      className="border-t pt-3 flex items-center justify-between"
                      style={{ borderColor: "#E0DFDB" }}
                    >
                      <span className="font-semibold" style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}>
                        Total
                      </span>
                      <span
                        className="font-serif font-bold text-2xl"
                        style={{ fontFamily: "Fraunces, Georgia, serif", color: "#1F1F1F", letterSpacing: "-0.03em" }}
                      >
                        ${ORDER_SUMMARY.total.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-5 space-y-2">
                    {["Free shipping on all orders", "30-day money-back guarantee", "Secure SSL checkout"].map((trust) => (
                      <div key={trust} className="flex items-center gap-2">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                          <path d="M20 6L9 17l-5-5" stroke="#3F5A45" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="text-xs" style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}>
                          {trust}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
